// Side-effect boot imports pinned FIRST so they evaluate ahead of the (now
// static) App graph — organize-imports hoists `import App` above any later
// import. Sentry + prod console suppression, then the theme stamp; both must
// precede the App modules (throw reporting / unthemed-paint hazards).
import './app/bootMonitoring';
import './app/bootTheme';

import loadable from '@loadable/component';
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './app/App';
import { AppConfig } from './config/appConfig';
import { installMediaExclusivity } from './lib/media/mediaExclusivity';
import { checkNativeMethodSupported, getAppType } from './lib/offline/OfflineAppHelper';
import { offlineAppService } from './lib/offline/offlineAppService';
import { installPreloadErrorGuard } from './lib/preloadErrorGuard';
import { initAnalytics } from './lib/telemetry/analytics';

// Spurious WebKit CSS-preload failures must not blank-page — armed before render.
installPreloadErrorGuard();

// One audio at a time across every surface — must be armed before any media
// renders, including <audio> injected into author HTML.
installMediaExclusivity();

// R15 (7.5): the v1 tree is deleted — BOTH flag branches render App (statically:
// the loadable(App) wrapper added a boot seam with no splitting benefit, APL-11850).
// Sentry init + prod console suppression live in ./app/bootMonitoring (pinned above).

// Global debugger utility
declare global {
  // eslint-disable-next-line no-var
  var apConsole: {
    log: (...args: any[]) => void;
    warn: (...args: any[]) => void;
    error: (...args: any[]) => void;
  };
}

// Determine app type for native bridge and sharing console.error to print in native app
const appType = window.navigator?.userAgent?.indexOf('AssessPrepMac') > -1 ? 'mac' : '';
if (appType === 'mac' && checkNativeMethodSupported(appType, 'printLog')) {
  const originalConsoleError = console.error.bind(console);
  console.error = (...args) => {
    // Always call the original console.error
    originalConsoleError(...args);
    try {
      const handler = window?.webkit?.messageHandlers?.macDownloadHelper;
      if (!handler?.postMessage) return;

      const firstArg = args[0];
      let value = '';

      if (firstArg && typeof firstArg === 'object' && 'message' in firstArg) {
        value = firstArg.message;
      } else if (typeof firstArg === 'string') {
        value = firstArg;
      } else {
        value = args
          .map((a) => {
            try {
              if (a instanceof Error && a.message) return a.message;
              if (a && typeof a === 'object' && 'message' in a && a.message) return a.message;
              if (typeof a === 'string') return a;
              return JSON.stringify(a);
            } catch (e) {
              return String(a);
            }
          })
          .join(' ');
      }

      offlineAppService.executeAppMethod({ key: 'printLog', value: JSON.stringify(value) });
    } catch (e) {
      //IMP: Silently ignore errors in the bridge to avoid recursive logging
    }
  };
}

// Device audit W1 (2026-07-22): the lockdown service defers init to first
// property access (TDZ-safe Proxy), but shipped mac/ios apps call the
// native→JS globals it registers (window.executePromise,
// window.executeSetLogOnQuit, …) unconditionally — a login-screen quit before
// any JS access left them undefined and skipped the mac-<12
// securityHasBeenInterrupted cleanup. The entry body runs after the whole
// import graph settles, so touching the Proxy here is past the TDZ hazard and
// restores v3's eager registration.
try {
  void offlineAppService.executeAppMethod;
} catch {
  // best-effort — a web context without native bridges must still boot
}

if (AppConfig.mode === 'development1' && typeof window !== 'undefined') {
  const reactScan = loadable.lib(() => import('react-scan'));
  reactScan.load().then((module: any) => {
    console.log('scan enabled');
    module.scan({
      enabled: false,
      log: true, // logs render info to console (default: false)
    });
  });
}
// Check if we're in development/debug mode
const isShowConsoleLogs = !['production', 'demo'].includes(import.meta.env.VITE_MODE || '');

// Create optimized global apConsole object
globalThis.apConsole = {
  log: (...args: any[]) => {
    if (isShowConsoleLogs) {
      console.log('[AP_DEBUG]', ...args);
    }
  },
  warn: (...args: any[]) => {
    if (isShowConsoleLogs) {
      console.warn('[AP_WARN]', ...args);
    }
  },
  error: (...args: any[]) => {
    console.error('[AP_ERROR]', ...args);
  },
};

// Check if the current browser is Safari
// const isSafari = getBrowserName() === 'Safari';

// PostHog — only enable for web app in specific environments, not China/embedded/native.
// Idle-init via dynamic import (APL-12271): posthog-js stays out of the entry chunk.
const isChina = AppConfig.region === 'china';
const isEmbedded = window.location.pathname.startsWith('/embed');
const posthogEnabledModes = ['development', 'staging', 'demo'];
const enablePosthog =
  !isChina &&
  getAppType() === 'web' &&
  !isEmbedded &&
  posthogEnabledModes.includes(import.meta.env.VITE_MODE || '');

if (enablePosthog) {
  initAnalytics(import.meta.env.VITE_PUBLIC_POSTHOG_KEY ?? '', {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    autocapture: false,
    capture_pageview: false,
    disable_session_recording: true,
    disable_surveys: true,
    capture_dead_clicks: false,
    disable_scroll_depth_capture: true,
  });
}

const container = document.getElementById('root') as HTMLElement;
const root = createRoot(container);

const appElement = <App />;

// Disable StrictMode for China region in development to prevent double-invocation issues
// When we subscribe to realtime events, becasue of StrictMode, the event will be invoked twice.
const shouldDisableStrictMode = isChina && AppConfig.mode === 'development';

root.render(
  shouldDisableStrictMode ? appElement : <React.StrictMode>{appElement}</React.StrictMode>
);
